import React, { Fragment, memo, useEffect, useMemo, useState } from "react";
import styles from "./InstallationForm.module.scss";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import CommonFormFieldWrapper, { getFormInputErrors } from "../../../../../common/components/form-field-wrapper/CommonFormFieldWrapper";
import FormWrapper from "../../../../../common/components/form-wrapper/FormWrapper";
import CommonIncrementInput from "../../../../../common/components/increment-input/CommonIncrementInput";
import RadioButtonField from "../../../../../common/components/radio-button-field/RadioButtonField";
import { Installation } from "../../../../../model/solar-recommendation-form/installation";
import { RoofType } from "../../../../../model/solar-recommendation-form/roofType";
import { useLazyRoofTypesQuery } from "../../../../../service/roof-type-service";

type Props = {
	initialInstallation?: Installation;
	addInstallation: (installation: Installation) => void;
	onBack: () => void;
};

const InstallationForm: React.FC<Props> = memo((props: Props) => {
	const { addInstallation, onBack } = props;
	const [getRoofTypes, { data: roofTypes = [] }] = useLazyRoofTypesQuery();

	const {
		register,
		watch,
		formState: { errors, isValid, isSubmitted },
		setValue,
		handleSubmit,
	} = useForm<Installation>({
		defaultValues: props.initialInstallation ?? {
			electricitySupply: undefined,
			authorization: false,
			peakUsagePeriod: undefined,
			conduciveness: false,
			premiseType: undefined,
			floors: undefined,
			roofType: undefined,
		},
		reValidateMode: "onChange",
		mode: "onTouched",
	});

	const watchPremiseType = watch("premiseType");
	const watchAuthorization = watch("authorization");
	const watchRoofType = watch("roofType");
	const watchFloors = watch("floors");
	const watchPeakUsage = watch("peakUsagePeriod");
	const watchConduciveness = watch("conduciveness");

	const peakUsageRegister = register("peakUsagePeriod", { required: watchPeakUsage === undefined && "Peak usage period is required" });
	const authorizationRegister = register("authorization", {
		required: watchAuthorization === undefined && "Authorization is required",
	});
	const conducivenessRegister = register("conduciveness", { required: watchConduciveness === undefined && "Conduciveness is required" });
	const premiseTypeRegister = register("premiseType", { required: watchAuthorization === true && "Premise is required" });
	const roofTypeRegister = register("roofType", { required: watchAuthorization === true && watchRoofType === undefined && "Roof Type is required" });
	const floorRegister = register("floors", {
		required: watchAuthorization === true && watchPremiseType === "Story Building" && "Floor are required",
		min: watchAuthorization === true && watchPremiseType === "Story Building" ? { value: 1, message: "Floors must be greater than 0" } : undefined,
	});

	useEffect(() => {
    const fetchRoofTypes = async () => {
      if (!watchPremiseType) return;

      try {
        await getRoofTypes(watchPremiseType);
        
        if (roofTypes.length > 0) {
          setValue("roofType", roofTypes[0]);
        }
      } catch (error: any) {
        toast.error(`Error fetching roof types: ${error.message}`);
        console.error("There was an error!", error);
      }
    };

    fetchRoofTypes();
  }, [getRoofTypes, watchPremiseType, roofTypes]);

	function submit(installation: Installation) {
		if (!watchAuthorization) {
			setValue("floors", 0);
			setValue("premiseType", "");
			setValue("roofType", undefined);
		}

		if (watchPremiseType === "Bungalow") setValue("floors", 0);
		addInstallation(installation);
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		const input = e.currentTarget.value;

		// Allow control keys like backspace, arrow keys, etc.
		const controlKeys = ["Backspace", "ArrowLeft", "ArrowRight", "Tab", "Delete"];

		// If the key pressed is not a digit and not a control key, prevent default
		if (!/\d/.test(e.key) && !controlKeys.includes(e.key)) {
			e.preventDefault();
		}

		// If the input already has 2 digits and the key pressed is a digit, prevent further input
		if (/\d/.test(e.key) && input.length >= 2) {
			e.preventDefault();
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setValue("electricitySupply", (e.target as any).value);
	};

	return (
		<FormWrapper
			titleSection={{ title: "Installation", subtitle: "Provide us the following details for installation..", buttonTitle: "Finish Setup" }}
			isNotValid={!isValid}
			isSubmitted={isSubmitted}
			onBack={onBack}
			onSaveAndContinue={handleSubmit(submit)}
		>
			<div className={styles.mainContainer}>
				<CommonFormFieldWrapper
					title="Electricity supply"
					description="How many hours of electricity do you get in a day?"
					currency="hr"
					required
					errors={getFormInputErrors(errors.electricitySupply?.message)}
				>
					<input
						{...register("electricitySupply", {
							required: "Electricity Supply is required",
							min: { value: 0, message: "Electricity Supply must be greater than 0" },
							max: { value: 24, message: "Value can't be more than 24" },
							maxLength: 2,
							valueAsNumber: true,
						})}
						placeholder="Enter hours"
						type="number"
						min={0}
						max={24}
						maxLength={2}
						onChange={handleChange}
						onKeyDown={handleKeyDown}
					/>
				</CommonFormFieldWrapper>
				<div className={`formFieldInlineContainer ${styles.formInlineRow}`}>
					<CommonFormFieldWrapper
						title="Peak usage period"
						description="When do you need your power supply?"
						borderNotVisible={true}
						required
						errors={getFormInputErrors(errors.peakUsagePeriod?.message)}
					>
						<RadioButtonField
							text="Day"
							registerProp={peakUsageRegister}
							initialValue={watch("peakUsagePeriod")}
							selectedValue={"Day"}
							onChangeValue={(value: string) => setValue("peakUsagePeriod", value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })}
						/>
						<RadioButtonField
							text="Night"
							registerProp={peakUsageRegister}
							initialValue={watch("peakUsagePeriod")}
							selectedValue={"Night"}
							onChangeValue={(value: string) => setValue("peakUsagePeriod", value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })}
						/>
					</CommonFormFieldWrapper>
				</div>
				<div className={`formFieldInlineContainer ${styles.formInlineRow}`}>
					<CommonFormFieldWrapper
						title="Conduciveness"
						description="Do you have a conducive place to store your inverter batteries?"
						borderNotVisible={true}
						required
						errors={getFormInputErrors(errors.conduciveness?.message)}
					>
						<RadioButtonField
							text="Yes"
							registerProp={conducivenessRegister}
							initialValue={watch("conduciveness")}
							selectedValue={true}
							onChangeValue={(value: boolean) => setValue("conduciveness", value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })}
						/>
						<RadioButtonField
							text="No"
							registerProp={conducivenessRegister}
							initialValue={watch("conduciveness")}
							selectedValue={false}
							onChangeValue={(value: boolean) => setValue("conduciveness", value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })}
						/>
					</CommonFormFieldWrapper>
				</div>
				{watchConduciveness && (
					<div className={`formFieldInlineContainer ${styles.formInlineRow}`}>
						<CommonFormFieldWrapper
							title="Authorization"
							description="Do you have access to install solar on your roof?"
							borderNotVisible={true}
							required
							errors={getFormInputErrors(errors.authorization?.message)}
						>
							<RadioButtonField
								text="Yes, I can"
								registerProp={authorizationRegister}
								initialValue={watch("authorization")}
								selectedValue={true}
								onChangeValue={(value: boolean) => setValue("authorization", value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })}
							/>
							<RadioButtonField
								text="No, I can't"
								registerProp={authorizationRegister}
								initialValue={watch("authorization")}
								selectedValue={false}
								onChangeValue={(value: boolean) => setValue("authorization", value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })}
							/>
						</CommonFormFieldWrapper>
					</div>
				)}
				{watchAuthorization && (
					<Fragment>
						<CommonFormFieldWrapper
							title="Premises"
							description="What type of premises do you reside?"
							borderNotVisible={true}
							required
							errors={getFormInputErrors(errors.premiseType?.message)}
						>
							<div className={styles.gridContainer1}>
								<RadioButtonField
									text="Bungalow"
									imageUrl="https://www.thespruce.com/thmb/zFg7mPetp_7ZVqcScgJHbc7PSTg=/1500x0/filters:no_upscale():max_bytes(150000):strip_icc()/GettyImages-523075374-8d27b929fdfc4702aab5c73f821a65de.jpg"
									imageClass={styles.containerImage}
									registerProp={premiseTypeRegister}
									initialValue={watch("premiseType")}
									selectedValue={"Bungalow"}
									onChangeValue={(value: string) => setValue("premiseType", value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })}
								/>
								<RadioButtonField
									text="Story building"
									imageUrl="https://storables.com/wp-content/uploads/2023/11/how-tall-is-a-3-story-apartment-building-1700108112.jpg"
									imageClass={styles.containerImage}
									registerProp={premiseTypeRegister}
									initialValue={watch("premiseType")}
									selectedValue={"Story Building"}
									onChangeValue={(value: string) => setValue("premiseType", value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })}
								/>
							</div>
						</CommonFormFieldWrapper>
						{watchPremiseType === "Story Building" && (
							<CommonFormFieldWrapper title="Floors" description="How many floors?" required errors={getFormInputErrors(errors.floors?.message)}>
								<div className={styles.incrementInputContainer}>
									<CommonIncrementInput
										value={watchFloors}
										inputProps={floorRegister}
										onChange={e => setValue("floors", e, { shouldValidate: true, shouldTouch: true, shouldDirty: true })}
									/>
								</div>
							</CommonFormFieldWrapper>
						)}
						{roofTypes.length > 0 && (
							<CommonFormFieldWrapper
								title="Roof Type"
								description="What type of roof does your building have?"
								borderNotVisible={true}
								required
								errors={getFormInputErrors(errors.roofType?.message)}
							>
								<div className={styles.gridContainer2}>
									{roofTypes.map(item => (
										<RadioButtonField
											key={item.id}
											text={item.name}
											imageUrl={item.iconUrl}
											registerProp={roofTypeRegister}
											initialValue={watch("roofType")?.id}
											selectedValue={item.id}
											onChangeValue={(value: RoofType) => {
												setValue("roofType", item, { shouldValidate: true, shouldTouch: true, shouldDirty: true });
											}}
										/>
									))}
								</div>
							</CommonFormFieldWrapper>
						)}
					</Fragment>
				)}
			</div>
		</FormWrapper>
	);
});

export default InstallationForm;
